import {
  Table,
  TableHead,
  TableRow,
  TableContainer,
  TableBody,
  TableCell,
  Checkbox,
  Paper,
  Button,
  Box,
  Modal,
  Typography,
  Backdrop,
} from "@mui/material";
import theme from "../../configs/theme";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

export default function VentaTableUI({
  headerData,
  bodyData,
  selectedRows,
  filters,
  showCheckbox,
  handleSelectRow,
  handlePay,
  openProductsModal,
  selectedProducts,
  selectedSubtotal,
  handleOpenProductsModal,
  handleCloseProductsModal,
  openPaymentModal,
  selectedPayments,
  selectedDiscount,
  handleOpenPaymentModal,
  handleClosePaymentModal,
}) {
  const filteredData = bodyData.filter((sale) => {
    const saleDate = new Date(sale.date);
    const today = new Date();
    let dateMatch = true;

    if (filters?.dateFilter) {
      const limit = new Date();
      switch (filters.dateFilter) {
        case "hoy":
          dateMatch = saleDate.toDateString() === today.toDateString();
          break;
        case "ayer":
          limit.setDate(today.getDate() - 1);
          dateMatch = saleDate.toDateString() === limit.toDateString();
          break;
        case "semana":
          limit.setDate(today.getDate() - 7);
          dateMatch = saleDate >= limit;
          break;
        case "15dias":
          limit.setDate(today.getDate() - 15);
          dateMatch = saleDate >= limit;
          break;
        case "mes":
          limit.setMonth(today.getMonth() - 1);
          dateMatch = saleDate >= limit;
          break;
        case "año":
          limit.setFullYear(today.getFullYear() - 1);
          dateMatch = saleDate >= limit;
          break;
        default:
          break;
      }
    }

    const totalMatch =
      sale.total >= filters.totalRange[0] &&
      sale.total <= filters.totalRange[1];
      const paymentMatch =
      !filters.paymentMethod ||
      sale.payments.some((p) => p.method === filters.paymentMethod);

    return dateMatch && totalMatch && paymentMatch;
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: showCheckbox ? "66%" : "64%",
        marginTop: 2,
        maxHeight: "66vh",
        overflowY: "auto",
        "&::-webkit-scrollbar-track": {
          background: theme.tableColors.header,
        },
      }}
    >
      <Table stickyHeader>
        <TableHead
          sx={{
            "& .MuiTableCell-root": {
              backgroundColor: theme.tableColors.header,
              fontSize: {
                md: theme.fontSizes.medium,
                sm: theme.fontSizes.small,
              },
              fontWeight: "bold",
              color: theme.colors.black,
              padding: "12px",
              borderBottom: "1px solid #ffffff",
            },
          }}
        >
          <TableRow>
            {headerData.map((item) => (
              <TableCell key={item}>{item}</TableCell>
            ))}
            {showCheckbox && (
              <TableCell
                sx={{
                  width: "6%",
                  "&.MuiTableCell-root": {
                    align: "center",
                    pt: 0,
                    pb: 0,
                  },
                }}
              >
                {headerData.length && (showCheckbox ? (headerData.includes("Eliminar") ? <DeleteIcon /> : <EditIcon />) : null)}
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody
          sx={{
            backgroundColor: theme.tableColors.body,
            "& .MuiTableCell-root": {
              fontSize: {
                md: theme.fontSizes.small,
                sm: theme.fontSizes.xsmall,
              },
              color: theme.colors.black,
              padding: "10px",
            },
          }}
        >
          {filteredData.map((row, index) => (
            <TableRow
              key={row.id}
              sx={{
                "&:hover": {
                  backgroundColor: theme.hoverColors.body,
                },
              }}
            >
              <TableCell>{filteredData.length - index}</TableCell>
              <TableCell>
                {new Date(row.date)
                  .toLocaleString("es-AR", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })
                  .replace(",", " -")}
              </TableCell>
              <TableCell>
                <Button onClick={() => handleOpenProductsModal(row.products, row.subtotal)}>
                  Ver más
                </Button>
              </TableCell>
              <TableCell>{row.total}</TableCell>
              <TableCell>
                <Button onClick={() => handleOpenPaymentModal(row.payments, row.discount)}>
                  Ver más
                </Button>
              </TableCell>
              <TableCell
                sx={{
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={row.isPaid}
                  onChange={() => handlePay(row.id)}
                  size="small"
                  sx={{
                    padding: 0,
                  }}
                />
              </TableCell>
              {showCheckbox && (
                <TableCell align="center">
                  <Checkbox
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                    size="small"
                    sx={{
                      padding: 0,
                    }}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Modal
        open={openProductsModal}
        onClose={handleCloseProductsModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 600,
          bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Detalles de productos
          </Typography>
          <ul>
            {selectedProducts.map((product, index) => {
              const isDecimal = product.sell_method === "Decimal";
              const reference = isDecimal
                ? product.quantity >= 1000
                  ? `${product.quantity / 1000} kg`
                  : `${product.quantity} gr`
                : product.quantity;
              
                const total = isDecimal
                    ? (product.price  * product.quantity / product.quantity_reference)
                    : product.price * product.quantity;

              return (
                <li key={index}>
                  {product.name} x {reference} - ${total}
                </li>
              );
            })}
          </ul>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Subtotal: ${selectedSubtotal}
          </Typography>
          <Box mt={2} textAlign="right">
            <Button
              onClick={handleCloseProductsModal}
              variant="contained"
              color="primary"
            >
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 600,
          bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Detalles de método de pago
          </Typography>
          <ul>
            {selectedPayments.map((payment, index) => {
              const adjustmentAmount = payment.amount * (payment.adjustment / 100);
              const finalAmount = payment.amount + adjustmentAmount;
            
              return (
                <li key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{payment.method} - ${payment.amount}</span>
                  {payment.adjustment !== 0 && (
                    <span>
                      {payment.adjustment > 0
                        ? `(Recargo: ${payment.adjustment}% = $${finalAmount})`
                        : `(Bonificación: ${Math.abs(payment.adjustment)}% = $${finalAmount})`}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {selectedDiscount > 0
              ? `Bonificación Extra: $${selectedDiscount}`
              : selectedDiscount < 0
                ? `Recargo Extra: $${Math.abs(selectedDiscount)}`
                : `Sin bonificación ni recargo extra`}
          </Typography>
          <Box mt={2} textAlign="right">
            <Button
              onClick={handleClosePaymentModal}
              variant="contained"
              color="primary"
            >
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>
    </TableContainer>
  );
}
